/** Default TTL for successful memory-cache entries (5 minutes). */
export const SAFE_QUERY_MEMORY_TTL_MS = 5 * 60 * 1000

/** Hard cap on memory cache entries to avoid unbounded growth. */
export const SAFE_QUERY_MEMORY_MAX_ENTRIES = 200

type MemoryEntry = {
  value: unknown
  expiresAt: number
  lastAccess: number
}

const memoryCache = new Map<string, MemoryEntry>()

function now(): number {
  return Date.now()
}

function pruneExpired(): void {
  const t = now()
  for (const [k, e] of memoryCache) {
    if (e.expiresAt <= t) memoryCache.delete(k)
  }
}

function evictLruOne(): void {
  let oldestKey: string | null = null
  let oldest = Number.POSITIVE_INFINITY
  for (const [k, e] of memoryCache) {
    if (e.lastAccess < oldest) {
      oldest = e.lastAccess
      oldestKey = k
    }
  }
  if (oldestKey != null) memoryCache.delete(oldestKey)
}

/** Distinguishes a cache miss from a stored `undefined` (never stored). */
export function memoryCacheTryGet(key: string): { hit: true; value: unknown } | { hit: false } {
  pruneExpired()
  const e = memoryCache.get(key)
  if (!e) return { hit: false }
  if (e.expiresAt <= now()) {
    memoryCache.delete(key)
    return { hit: false }
  }
  e.lastAccess = now()
  return { hit: true, value: e.value }
}

export function memoryCacheSet(key: string, value: unknown, ttlMs: number = SAFE_QUERY_MEMORY_TTL_MS): void {
  pruneExpired()
  const t = now()
  memoryCache.set(key, { value, expiresAt: t + ttlMs, lastAccess: t })
  while (memoryCache.size > SAFE_QUERY_MEMORY_MAX_ENTRIES) {
    evictLruOne()
  }
}

export function memoryCacheDelete(key: string): void {
  memoryCache.delete(key)
}

export function memoryCacheDeletePrefix(prefix: string): void {
  for (const k of [...memoryCache.keys()]) {
    if (k.startsWith(prefix)) memoryCache.delete(k)
  }
}

/** Test / diagnostics: current size after pruning expired. */
export function memoryCacheSize(): number {
  pruneExpired()
  return memoryCache.size
}

/** Snapshot of cache keys (after pruning expired). */
export function memoryCacheKeys(): string[] {
  pruneExpired()
  return [...memoryCache.keys()]
}
